import { CheckCircle2 } from "lucide-react";
import PropTypes from "prop-types";
import { formatCurrency } from "@src/utility/helpers/utils";
import RevertDialog from "./revert-dialog";

/**
 * The brand's side of the debit note flow, for the preview footer: reject or
 * acknowledge an issued note, approve an acknowledged one, and walk either
 * step back one stage.
 */
const DebitNoteActions = ({ note, onReject, onAcknowledge, onApprove, onUndoAcknowledge, onWithdrawApproval }) => {
    if (note.status === "issued") {
        return (
            <>
                <RevertDialog
                    title="Reject debit note"
                    triggerLabel="Reject"
                    actionLabel="Reject"
                    description={`${note.debit_note_number} is declined and closed. A new debit note has to be raised instead.`}
                    onConfirm={(reason) => onReject(note, reason)} />
                <button
                    type="button"
                    onClick={() => onAcknowledge(note)}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-navy px-4 py-2 text-sm font-medium text-white hover:bg-navy/90">
                    <CheckCircle2 className="h-4 w-4" />
                    Acknowledge receipt
                </button>
            </>
        );
    }

    if (note.status === "acknowledged") {
        return (
            <>
                <RevertDialog
                    title="Undo acknowledgement"
                    triggerLabel="Undo acknowledgement"
                    actionLabel="Undo acknowledgement"
                    description={`Send ${note.debit_note_number} back to Issued, withdrawing your acknowledgement so it can be reviewed again before re-acknowledging.`}
                    onConfirm={(reason) => onUndoAcknowledge(note, reason)} />
                <button
                    type="button"
                    onClick={() => onApprove(note)}
                    title={`On approval, transfer ${formatCurrency(note.amount, note.currency)} offline to Nook Pay using the bank details in the note.`}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90">
                    <CheckCircle2 className="h-4 w-4" />
                    Approve &amp; pay offline
                </button>
            </>
        );
    }

    if (note.status === "approved") {
        return (
            <RevertDialog
                title="Withdraw approval"
                triggerLabel="Withdraw approval"
                actionLabel="Withdraw approval"
                description={`Send ${note.debit_note_number} back to Acknowledged so it can be reviewed or corrected before approving again.`}
                onConfirm={(reason) => onWithdrawApproval(note, reason)} />
        );
    }

    return null;
};

DebitNoteActions.propTypes = {
    note              : PropTypes.object.isRequired,
    onReject          : PropTypes.func.isRequired,
    onAcknowledge     : PropTypes.func.isRequired,
    onApprove         : PropTypes.func.isRequired,
    onUndoAcknowledge : PropTypes.func.isRequired,
    onWithdrawApproval: PropTypes.func.isRequired
};

export default DebitNoteActions;
